/**
 * Quote Extractor API client.
 *
 * Stateless POST to /quote-extractor/extract. Accepts pasted text or an uploaded
 * .txt/.docx/.pdf file (mutually exclusive) and returns 5-10 ranked quotes.
 *
 * See specs/024-quote-extractor/contracts/quote-extractor-api.yaml for the
 * authoritative contract.
 */

import { getAuthConfig } from '../config/auth';

export type SourceType = 'txt' | 'docx' | 'pdf' | 'pasted';

export interface SourceMetadata {
  type: SourceType;
  byte_length?: number;
  word_count: number;
}

export interface Quote {
  text: string;
  score: number;
  rationale: string;
  source_context: string;
  position: number;
  speaker: string | null;
}

export interface ExtractResponse {
  title: string;
  source_metadata: SourceMetadata;
  quotes: Quote[];
  no_quotes_reason: string | null;
}

export interface ExtractInput {
  text?: string;
  file?: File;
  titleHint?: string;
}

export type ExtractErrorKind =
  | 'validation'
  | 'payload_too_large'
  | 'unauthorized'
  | 'service_unavailable'
  | 'network'
  | 'unknown';

export class ExtractError extends Error {
  readonly kind: ExtractErrorKind;
  readonly status: number;
  readonly canRetry: boolean;

  constructor(kind: ExtractErrorKind, status: number, message: string, canRetry: boolean) {
    super(message);
    this.name = 'ExtractError';
    this.kind = kind;
    this.status = status;
    this.canRetry = canRetry;
  }
}

function getApiBaseUrl(): string {
  const config = getAuthConfig();
  if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    try {
      const stored = localStorage.getItem('vibe-dev-mode-config');
      if (stored) {
        const devConfig = JSON.parse(stored);
        if (devConfig.apiTarget === 'local') {
          return 'http://localhost:8000';
        }
      }
    } catch {
      // ignore
    }
  }
  return config.apiUrl;
}

function classifyError(status: number): { kind: ExtractErrorKind; canRetry: boolean; message: string } {
  switch (status) {
    case 400:
    case 422:
      return {
        kind: 'validation',
        canRetry: false,
        message: 'Invalid input — please check your text or file and try again.',
      };
    case 401:
    case 403:
      return {
        kind: 'unauthorized',
        canRetry: false,
        message: 'Your session has expired. Please sign in again.',
      };
    case 413:
      return {
        kind: 'payload_too_large',
        canRetry: false,
        message: 'Document is too large for the server to accept.',
      };
    case 503:
      return {
        kind: 'service_unavailable',
        canRetry: true,
        message: 'Extraction service is temporarily unavailable. Please retry.',
      };
    default:
      if (status >= 500) {
        return {
          kind: 'service_unavailable',
          canRetry: true,
          message: 'Something went wrong on our end. Please retry.',
        };
      }
      return {
        kind: 'unknown',
        canRetry: false,
        message: 'An unexpected error occurred.',
      };
  }
}

export async function extractQuotes(
  input: ExtractInput,
  token: string | null,
  signal?: AbortSignal,
): Promise<ExtractResponse> {
  if (!input.text && !input.file) {
    throw new ExtractError('validation', 0, 'Provide either pasted text or a file.', false);
  }
  if (input.text && input.file) {
    throw new ExtractError(
      'validation',
      0,
      'Provide either pasted text or a file, not both.',
      false,
    );
  }

  const formData = new FormData();
  if (input.text) {
    formData.append('text', input.text);
  }
  if (input.file) {
    formData.append('file', input.file);
  }
  if (input.titleHint) {
    formData.append('title_hint', input.titleHint);
  }

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const url = `${getApiBaseUrl()}/quote-extractor/extract`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      throw new ExtractError('network', 0, 'Request was cancelled.', false);
    }
    throw new ExtractError(
      'network',
      0,
      'Unable to reach the extraction service. Check your connection.',
      true,
    );
  }

  if (!response.ok) {
    const { kind, canRetry, message } = classifyError(response.status);
    let detail = message;
    try {
      const body = await response.json();
      if (body && typeof body.detail === 'string') {
        detail = body.detail;
      }
    } catch {
      // ignore
    }
    throw new ExtractError(kind, response.status, detail, canRetry);
  }

  const data = (await response.json()) as ExtractResponse;
  return data;
}
